import { useCallback } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import type { AxiosError } from 'axios'
import { isAxiosError } from 'axios'
import ReCAPTCHA from 'react-google-recaptcha'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import { AppRoutes, CAPTCHA_SITE_KEY } from '@/utils/constants'

import { useAccount } from '../hooks/useAccounts'
import type { RegisterFormValues } from '../types'
import { registerFormSchema } from '../types'

export const RegisterForm = () => {
  const { register } = useAccount()

  const form = useForm<RegisterFormValues>({
    mode: 'onChange',
    resolver: zodResolver(registerFormSchema),
  })

  const {
    handleSubmit,
    setError,
    formState: { errors },
  } = form

  const onSubmit = useCallback(
    async (values: RegisterFormValues) => {
      await register.mutateAsync(values).catch((error: AxiosError) => {
        if (!isAxiosError(error)) {
          return
        }

        setError('root', {
          type: 'server',
          message: String(error.response?.data),
        })
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return (
    <div>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {errors.root && (
            <div className="mb-4 w-full rounded-md bg-danger-light py-1.5 text-center text-sm text-danger-default">
              {errors.root?.message}
            </div>
          )}

          <div className="mb-14 flex flex-col gap-6">
            <FormField
              name="name"
              label="Name"
              placeholder="Max Mustermann"
            />

            <FormField
              name="email"
              label="Email Address"
              placeholder="max@mustermann.de"
            />

            <FormField
              name="password"
              label="Password"
              placeholder="Password"
              type="password"
              isPassword
            />

            <FormField
              name="confirmPassword"
              label="Confirm Password"
              placeholder="Repeat Password"
              isPassword
              type="password"
            />

            <Controller
              control={form.control}
              name="g-recaptcha-response"
              render={({ field }) => (
                <ReCAPTCHA
                  sitekey={CAPTCHA_SITE_KEY}
                  onChange={(value) => {
                    if (!value) {
                      return
                    }

                    field.onChange(value)
                  }}
                />
              )}
            />
          </div>

          <Button
            className="w-full"
            isLoading={register.isLoading}
          >
            Sign Up
          </Button>

          <div className="mt-3 flex items-center justify-center gap-1 px-14 text-sm font-normal text-text-sub">
            <p>Already have an account? </p>
            <Link
              to={AppRoutes.Auth.Login.asUrl()}
              className="text-sm text-primary-500 underline"
            >
              Log In
            </Link>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
